import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';
import { Text, Theme, Button, FilterClaimsModal, Icons, Container, Content, Title, i18n, tokens, localizationMaps, MessageCard } from '@simplybenefits/sb-ui-library';
import { styled } from '@mui/system';
import Dial from './Dial.js';
import ClaimBlock from '../Claims/ClaimBlock.js';
import SubmitClaim from '../Claims/SubmitClaim/index.js';
import { numberWithCommas } from '../../services/helpers.js';
import { setCurrentSidebarItem } from '../../actions/user.js';
import { getHSAClaims, getLSAClaims } from '../../actions/spendingAccounts.js';
import NoBankingInfoSetUp from '../../components/NoBankingInfoSetUp.js';

const SpendingColumns = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 35,
    [theme.breakpoints.down(100)]: {
        flexDirection: 'column'
    }
}));

class SpendingAccounts extends Component {
    state = {
        showHeathBenefits: true,
        showLifestyleBenefits: true,
        submitLifestyleClaimDialogOpen: false,
        submitHealthClaimDialogOpen: false,
        isDialOpen: false,
        healthFilterClaimsDialogOpen: false,
        lifestyleFilterClaimsDialogOpen: false,
        healthActiveSort: {},
        lifestyleActiveSort: {},
        healthActiveFilters: {},
        lifestyleActiveFilters: {},
        healthAmountOfActiveFilters: 0,
        lifestyleAmountOfActiveFilters: 0
    };

    componentDidMount() {
        if (this.props.meta.isDependent || this.props.meta.isSurvivorBenefitParent) {
            window.location.href = '/';
        }

        this.props.dispatch(setCurrentSidebarItem('spending-accounts'));
    }

    componentWillUnmount() {
        // Clean up the filters in redux
        if (this.state.healthAmountOfActiveFilters > 0) {
            this._resetFilters('health');
        }

        if (this.state.lifestyleAmountOfActiveFilters > 0) {
            this._resetFilters('lifestyle');
        }
    }

    _toggleHealthBenefits = () => {
        this.setState({
            showHeathBenefits: !this.state.showHeathBenefits
        });
    };

    _toggleLifestyleBenefits = () => {
        this.setState({
            showLifestyleBenefits: !this.state.showLifestyleBenefits
        });
    };

    _toggleSubmitLifestyleClaim = () => {
        this.setState({
            submitLifestyleClaimDialogOpen: !this.state.submitLifestyleClaimDialogOpen
        });
    };

    _toggleSubmitHealthClaim = () => {
        this.setState({
            submitHealthClaimDialogOpen: !this.state.submitHealthClaimDialogOpen
        });
    };

    _toggleDial = () => {
        this.setState({
            isDialOpen: !this.state.isDialOpen
        });
    };

    _toggleFiltersModal = (type, value = null) => {
        this.setState({
            [`${ type }FilterClaimsDialogOpen`]: value === null ? !this.state[`${ type }FilterClaimsDialogOpen`] : value
        });
    };

    _applyFilters = (type, filters, sort) => {
        if (type === 'health') {
            this.props.dispatch(getHSAClaims(filters, sort));
        } else {
            this.props.dispatch(getLSAClaims(filters, sort));
        }

        // Count all filters that are not their default value
        let amountOfActiveFilters = 0;

        amountOfActiveFilters += filters.patient === 'All Patients' ? 0 : 1;
        amountOfActiveFilters += filters.category === 'All Categories' ? 0 : 1;
        amountOfActiveFilters += filters.service === 'All Service Types' ? 0 : 1;
        amountOfActiveFilters += filters.year === 'All Years' ? 0 : 1;
        amountOfActiveFilters += sort === false ? 0 : 1;

        // set active filters to save for later
        this.setState({
            [`${ type }ActiveFilters`]: filters,
            [`${ type }ActiveSort`]: sort,
            [`${ type }AmountOfActiveFilters`]: amountOfActiveFilters
        });

        this._toggleFiltersModal(type, false);
    };

    _resetFilters = (type) => {
        if (type === 'health') {
            this.props.dispatch(getHSAClaims({}, false, 0));
        } else {
            this.props.dispatch(getLSAClaims({}, false, 0));
        }

        this.setState({
            [`${ type }ActiveFilters`]: {},
            [`${ type }ActiveSort`]: false,
            [`${ type }AmountOfActiveFilters`]: 0
        });

        this._toggleFiltersModal(type, false);
    };

    render() {
        if (this.props.meta.isDependent) {
            return null;
        }

        const { healthItems, lifestyleItems, healthRemaining, healthRollover, lifestyleRemaining, healthTotal, lifestyleTotal, lifestyleRollover, whitelabel, coverage, meta, lsaClaimTypes } = this.props;
        const { isDependent } = this.props.meta;

        const { showHeathBenefits, showLifestyleBenefits } = this.state;

        const healthPercentage = (1 - (healthRemaining / healthTotal)) * 100;
        const lifestylePercentage = (1 - (lifestyleRemaining / lifestyleTotal)) * 100;

        const half = Math.ceil(lsaClaimTypes?.length / 2);
        const left = lsaClaimTypes?.slice(0, half);
        const right = lsaClaimTypes?.slice(half, lsaClaimTypes?.length);

        const styles = {
            cards: {
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: 1000,
                margin: 'auto'
            },
            card: {
                position: 'relative',
                width: 300,
                height: 300,
                minWidth: 280,
                minHeight: 300,
                margin: 20
            },
            detailsBlock: {
                position: 'absolute',
                width: '100%',
                bottom: 40,
                cursor: 'pointer'
            },
            claimsList: {
                display: 'block',
                margin: 'auto',
                position: 'relative',
                maxWidth: 1000
            },
            textBlock: {
                display: 'flex',
                flexDirection: 'row',
                margin: '20px 0 10px 0'
            },
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center'
            },
            spendingRow: {
                display: 'flex',
                flexDirection: 'column',
                minWidth: '50%',
                maxWidth: '50%'
            }
        };

        return (
            <Container>
                <Content>
                    <div style={ styles.titleBlock }>
                        <Title title={ i18n.t(tokens.titles.spendingAccounts) } customStyle={{ flex: 1 }} />

                        {
                            (coverage.spendingAccounts.health.hasBenefit === 1 && coverage.spendingAccounts.meta.isEligible === 1 && (Number(meta.paymentsSetup) === 1 || Number(meta.requiresCheque) === 1) && !meta.claimsDisabledForRollover) &&
                                <Button
                                    color="primary"
                                    customStyle={{
                                        height: 50,
                                        width: 180,
                                        marginRight: 1,
                                        textTransform: "capitalize",
                                        fontSize: 16
                                    }}
                                    onClick={ this._toggleSubmitHealthClaim }
                                    id="create-health-claim"
                                >
                                    { i18n.t(tokens.buttons["health claim"]) }
                                </Button>
                        }

                        {
                            (coverage.spendingAccounts.lifestyle.hasBenefit === 1 && coverage.spendingAccounts.meta.isEligible === 1 && (Number(meta.paymentsSetup) === 1 || Number(meta.requiresCheque) === 1) && !meta.claimsDisabledForRollover) &&
                                <Button
                                    id="view-history"
                                    color="white"
                                    noHover
                                    customStyle={{
                                        height: 50,
                                        width: 180,
                                        textTransform: "capitalize",
                                        fontSize: 16
                                    }}
                                    onClick={ this._toggleSubmitLifestyleClaim }
                                >
                                    { i18n.t(tokens.buttons["lifestyle claim"])}
                                </Button>
                        }

                        {
                            coverage.spendingAccounts.meta.isEligible === 0 &&
                                <Text small bold>
                                    { i18n.t(tokens.labels.spendingAccounts.youAreCurrentlyIneligibleForThisBenefit) }
                                </Text>
                        }
                    </div>

                    {
                        meta.claimsDisabledForRollover ?
                            <MessageCard
                                title={ i18n.t(tokens.labels.spendingAccounts.claimsTemporarilyDisabled) }
                                subtitle={ i18n.t(tokens.labels.spendingAccounts.claimSubmissionDisabledNote, { firstname: meta.firstname }) }
                                isError={ true }
                                small
                            />
                            :
                            Number(meta.paymentsSetup) === 0 && Number(meta.requiresCheque) === 0 ?
                                <NoBankingInfoSetUp
                                    isDependent={ isDependent }
                                    addBanking={ () => this.props.history.push('/my-account') }
                                />
                                :
                                <React.Fragment>
                                    <div style={ styles.cards }>
                                        {
                                            coverage.spendingAccounts.health.hasBenefit === 1 &&
                                                <div style={ styles.card }>
                                                    <Dial percentage={healthPercentage} >
                                                        <Text
                                                            extraLarge
                                                            bold
                                                            color={ whitelabel.primaryColor || Theme.colors.skyBlue}
                                                        >
                                                            { i18n.t(tokens.labels.health) }
                                                        </Text>

                                                        <Text largest bold margin="10px 0 0 0">{ `$${ numberWithCommas(healthRemaining) }` }</Text>
                                                        <Text
                                                            mediumLarge
                                                            color={whitelabel.name ? Theme.colors.mediumDarkGrey : Theme.colors.brandGreen}
                                                            align="center"
                                                            margin="-10px 0 0 0"
                                                        >
                                                            { i18n.t(tokens.labels.spendingAccounts.remaining) }
                                                        </Text>
                                                    </Dial>

                                                    <div id="seeHealth" style={ styles.detailsBlock } onClick={this._toggleHealthBenefits}>
                                                        <Text tiny align="center">{ showHeathBenefits ? i18n.t(tokens.labels.spendingAccounts.hideDetails) : i18n.t(tokens.labels.spendingAccounts.seeDetails) }</Text>
                                                    </div>
                                                </div>
                                        }

                                        {
                                            coverage.spendingAccounts.lifestyle.hasBenefit === 1 &&
                                                <div style={ styles.card }>
                                                    <Dial percentage={lifestylePercentage}>
                                                        <Text
                                                            extraLarge
                                                            bold
                                                            color={ whitelabel.primaryColor || Theme.colors.skyBlue}
                                                        >
                                                            { i18n.t(tokens.labels.lifestyle) }
                                                        </Text>

                                                        <Text largest bold margin="10px 0 0 0">{ `$${ numberWithCommas(lifestyleRemaining) }` }</Text>
                                                        <Text
                                                            mediumLarge
                                                            color={whitelabel.name ? Theme.colors.mediumDarkGrey : Theme.colors.brandGreen}
                                                            align="center"
                                                            margin="-10px 0 0 0"
                                                        >
                                                            { i18n.t(tokens.labels.spendingAccounts.remaining) }
                                                        </Text>
                                                    </Dial>

                                                    <div id="seeLifestyle" style={ styles.detailsBlock } onClick={this._toggleLifestyleBenefits}>
                                                        <Text tiny align="center">{ showLifestyleBenefits ? i18n.t(tokens.labels.spendingAccounts.hideDetails) : i18n.t(tokens.labels.spendingAccounts.seeDetails) }</Text>
                                                    </div>
                                                </div>
                                        }

                                        {
                                            coverage.spendingAccounts.meta.isProrated === 1 &&
                                                <Text small margin="10px 0 20px 0">{ i18n.t(tokens.labels.spendingAccounts.proratedNote, { proratedBy: coverage.spendingAccounts.meta.proratedBy }) }</Text>
                                        }

                                        {
                                            coverage.spendingAccounts.meta.isQuarterlyAllowance === 1 &&
                                                <Text small margin="10px 0 20px 0">{ i18n.t(tokens.labels.spendingAccounts.quarterlyAllowanceNote) }</Text>
                                        }
                                    </div>

                                    <Content customStyle={ styles.claimsList } >
                                        {
                                            (this.state.showHeathBenefits && coverage.spendingAccounts.health.hasBenefit === 1) &&
                                            <React.Fragment>
                                                {
                                                    healthItems.length !== 0 || this.state.healthAmountOfActiveFilters !== 0 ?
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                            <Button
                                                                noHover
                                                                color={ this.state.healthAmountOfActiveFilters > 0 ? 'primary' : 'white' }
                                                                icon="filter-outline"
                                                                onClick={ () => this._toggleFiltersModal('health', true) }
                                                                id="filterclaims"
                                                                customStyle={{
                                                                    textTransform: "capitalize",
                                                                    fontSize: 18
                                                                }}
                                                            >
                                                                <Icon style={{ marginRight: 8, color: 'inherit' }}>
                                                                    { Icons.Filter }
                                                                </Icon>

                                                                {
                                                                    this.state.healthAmountOfActiveFilters > 0 ?
                                                                        i18n.t(tokens.buttons.applied, { count: this.state.healthAmountOfActiveFilters, numFilters: this.state.healthAmountOfActiveFilters })
                                                                        :
                                                                        i18n.t(tokens.buttons.filter)
                                                                }
                                                            </Button>
                                                        </div>
                                                        :
                                                        null
                                                }

                                                <div style={ styles.textBlock }>
                                                    <Text
                                                        medium
                                                        bold
                                                        customStyle={{ flex: 1 }}
                                                    >{ i18n.t(tokens.titles.healthSpending) }</Text>
                                                    <Text small color={Theme.colors.mediumDarkBlue}>Maximum: { healthRollover ? `$${ numberWithCommas(healthTotal - healthRollover) }` : `$${ numberWithCommas(healthTotal) }`}</Text>

                                                    {
                                                        healthRollover &&
                                                            <React.Fragment>
                                                                <Text small color={Theme.colors.mediumDarkBlue} customStyle={{ marginLeft: 5, marginRight: 5 }}>|</Text>
                                                                <Text small color={Theme.colors.mediumDarkBlue}>{ `Rollover: ${ numberWithCommas(healthRollover) }` }</Text>
                                                            </React.Fragment>
                                                    }
                                                </div>

                                                {
                                                    healthItems.length === 0 ?
                                                        this.state.healthAmountOfActiveFilters === 0 ?
                                                            <Text small margin={'10px 0 0 0'}>{ i18n.t(tokens.labels.spendingAccounts.noHealthSpendingUsage) }</Text>
                                                            :
                                                            <div>
                                                                <Text small margin={'10px 0 0 0'}>{ i18n.t(tokens.labels.claims.noResultsFound) } <span onClick={ () => this._resetFilters('health') } style={{ textDecoration: 'underline', cursor: 'pointer' }}>{ i18n.t(tokens.labels.claims.clearFilters) }</span></Text>
                                                            </div>
                                                        :
                                                        <>
                                                            <ClaimBlock isHeader isSpendingAccount />

                                                            {
                                                                healthItems.map((item) => (
                                                                    <ClaimBlock
                                                                        key={item.claimID }
                                                                        user={item.name}
                                                                        uid={item.uid}
                                                                        amount={Number(item.amountUsed)}
                                                                        date={ item.serviceDate || item.dateAdded }
                                                                        type={item.type}
                                                                        status={item.status}
                                                                        isSpendingAccount
                                                                    />
                                                                ))
                                                            }
                                                        </>
                                                }
                                            </React.Fragment>
                                        }

                                        {
                                            (this.state.showLifestyleBenefits && coverage.spendingAccounts.lifestyle.hasBenefit === 1) &&
                                                <div style={ styles.details }>
                                                    {
                                                        lifestyleItems.length !== 0 || this.state.lifestyleAmountOfActiveFilters !== 0 ?
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                <Button
                                                                    color={ this.state.lifestyleAmountOfActiveFilters > 0 ? 'primary' : 'white' }
                                                                    icon="filter-outline"
                                                                    onClick={ () => this._toggleFiltersModal('lifestyle', true) }
                                                                    id="filterclaims"
                                                                    customStyle={{
                                                                        textTransform: "capitalize",
                                                                        fontSize: 18,
                                                                        marginTop: 2.5
                                                                    }}
                                                                >
                                                                    <Icon
                                                                        style={{ marginRight: 8, color: 'inherit' }}
                                                                    >
                                                                        { Icons.Filter }
                                                                    </Icon>

                                                                    {
                                                                        this.state.lifestyleAmountOfActiveFilters > 0 ?
                                                                            i18n.t(tokens.buttons.applied, { count: this.state.lifestyleAmountOfActiveFilters, numFilters: this.state.lifestyleAmountOfActiveFilters })
                                                                            :
                                                                            i18n.t(tokens.buttons.filter)
                                                                    }
                                                                </Button>
                                                            </div>
                                                            :
                                                            null
                                                    }

                                                    <div style={ styles.textBlock }>
                                                        <Text
                                                            medium
                                                            bold
                                                            customStyle={{ flex: 1 }}
                                                        >
                                                            { i18n.t(tokens.titles.lifestyleSpending) }
                                                        </Text>
                                                        <Text small color={Theme.colors.mediumDarkBlue}>Maximum: { lifestyleRollover ? `$${ numberWithCommas(lifestyleTotal - lifestyleRollover) }` : `$${ numberWithCommas(lifestyleTotal) }`}</Text>

                                                        {
                                                            lifestyleRollover &&
                                                            <React.Fragment>
                                                                <Text small color={Theme.colors.mediumDarkBlue} customStyle={{ marginLeft: 5, marginRight: 5 }}>|</Text>
                                                                <Text small color={Theme.colors.mediumDarkBlue}>{ `Rollover: ${ numberWithCommas(lifestyleRollover) }` }</Text>
                                                            </React.Fragment>
                                                        }
                                                    </div>
                                                    {
                                                        lifestyleItems.length === 0 ?
                                                            this.state.lifestyleAmountOfActiveFilters === 0 ?
                                                                <Text small margin={'10px 0 0 0'}>{ i18n.t(tokens.labels.claims.noLifestyleClaims) }</Text>
                                                                :
                                                                <div>
                                                                    <Text small margin={'10px 0 0 0'}>{ i18n.t(tokens.labels.claims.noResultsFound) } <span onClick={ () => this._resetFilters('lifestyle') } style={{ textDecoration: 'underline', cursor: 'pointer' }}>{ i18n.t(tokens.labels.claims.clearFilters) }</span></Text>
                                                                </div>
                                                            :
                                                            <>
                                                                <ClaimBlock isHeader isSpendingAccount />

                                                                {
                                                                    lifestyleItems.map((item) => (
                                                                        <ClaimBlock
                                                                            key={item.claimID }
                                                                            user={item.name}
                                                                            uid={item.uid}
                                                                            amount={item.amountUsed}
                                                                            date={ item.serviceDate || item.dateAdded }
                                                                            type={item.type}
                                                                            status={item.status}
                                                                            isSpendingAccount
                                                                        />
                                                                    ))
                                                                }
                                                            </>
                                                    }

                                                    {
                                                        lsaClaimTypes.length > 0 ?
                                                            <React.Fragment>
                                                                <Text small bold margin="20px 0px 10px 0px">
                                                                    { i18n.t(tokens.labels.coverage.lifestyleSpendingClaims) }
                                                                </Text>

                                                                <SpendingColumns>
                                                                    <div style={ styles.spendingRow }>
                                                                        {left.map((item) => <Text key={ item } small margin="0px 20px 0px 0px">{ localizationMaps.fromEnglish("benefits", item) }</Text>)}
                                                                    </div>

                                                                    <div style={styles.spendingRow}>
                                                                        {right.map((item) => <Text key={ item } small margin="0px 20px 0px 0px">{ localizationMaps.fromEnglish("benefits", item) }</Text>)}
                                                                    </div>
                                                                </SpendingColumns>
                                                            </React.Fragment>
                                                            : null
                                                    }
                                                </div>
                                        }
                                    </Content>
                                </React.Fragment>
                    }
                </Content>

                {
                    this.state.submitLifestyleClaimDialogOpen &&
                        <SubmitClaim close={this._toggleSubmitLifestyleClaim} type="Lifestyle" />
                }

                {
                    this.state.submitHealthClaimDialogOpen &&
                        <SubmitClaim close={this._toggleSubmitHealthClaim} type="HealthSpending" />
                }

                {
                    this.state.healthFilterClaimsDialogOpen &&
                        <FilterClaimsModal
                            applyFilters={ (filters, sort) => this._applyFilters('health', filters, sort) }
                            close={ () => this._toggleFiltersModal('health', false) }
                            resetFilters={ () => this._resetFilters('health') }
                            activeFilters={ this.state.healthActiveFilters }
                            activeSort={ this.state.healthActiveSort }
                            user={ this.props.meta }
                            dependents={ this.props.dependents }
                            hsaClaimTypes={ this.props.hsaClaimTypes }
                        />
                }

                {
                    this.state.lifestyleFilterClaimsDialogOpen &&
                        <FilterClaimsModal
                            applyFilters={ (filters, sort) => this._applyFilters('lifestyle', filters, sort) }
                            close={ () => this._toggleFiltersModal('lifestyle', false) }
                            resetFilters={ () => this._resetFilters('lifestyle') }
                            activeFilters={ this.state.lifestyleActiveFilters }
                            activeSort={ this.state.lifestyleActiveSort }
                            user={ this.props.meta }
                            dependents={ this.props.dependents }
                            lsaClaimTypes={ this.props.lsaClaimTypes }
                            isLifeStyle={ true }
                        />
                }

                <span id="spending-accounts" />
            </Container>
        );
    }
}


SpendingAccounts.propTypes = {
    loading: PropTypes.bool,
    healthItems: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array
    ]),
    healthTotal: PropTypes.number,
    healthRemaining: PropTypes.number,
    lifestyleItems: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array
    ]),
    lifestyleTotal: PropTypes.number,
    lifestyleRemaining: PropTypes.number,
    whitelabel: PropTypes.object,
    meta: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loading: state.spendingAccounts.loading,
    coverage: state.coverage.data,
    hsaClaimTypes: state.usage.data.claimTypes,
    healthItems: state.spendingAccounts.health.items,
    healthTotal: state.spendingAccounts.health.total,
    healthRemaining: state.spendingAccounts.health.remaining,
    healthRollover: state.spendingAccounts.health.rollover,
    lifestyleItems: state.spendingAccounts.lifestyle.items,
    lsaClaimTypes: state.spendingAccounts.lsaClaimTypes,
    lifestyleTotal: state.spendingAccounts.lifestyle.total,
    lifestyleRemaining: state.spendingAccounts.lifestyle.remaining,
    lifestyleRollover: state.spendingAccounts.lifestyle.rollover,
    whitelabel: state.whitelabel,
    meta: state.user.meta,
    dependents: state.user.dependents
});

export default connect(mapStateToProps)(SpendingAccounts);